import Phaser from 'phaser';
import type { Variant } from '@/domain/types/common';
import { PetAnimator } from '@/game/fx/PetAnimator';
import {
  variantScaleMult,
  applyVariantFx,
  destroyVariantFx,
  type VariantFxRefs,
} from '@/game/fx/variantFx';
import type { WorldInteractionController } from '@/game/ui/WorldInteractionController';

/**
 * StallPet — wizualny sprite peta wystawionego w zagrodzie w plazie (5.B.6a).
 *
 * Cel designerski (D18 z DESIGN_TRADING_PLAZA.md): „pety biegają w zagrodach,
 * idle bob + losowy wander w boundsach budki, BEZ AI walki". Tła zagrody to
 * scenografia handlu — żywa wystawka przyciągająca wzrok.
 *
 * Różnice vs `Pet.ts` (drużyna gracza):
 *  - **Brak FSM walki** (IDLE_FOLLOW/MOVING/ATTACKING/RETURNING) — pet po
 *    prostu spaceruje wewnątrz paddocku.
 *  - **Brak targetowania destructibles**, brak damage, brak attack hits.
 *  - **Pozycje czysto wizualne** — żaden klient NIE syncuje pozycji petów
 *    w paddoku przez Colyseus. Każda przeglądarka losuje własny wander
 *    niezależnie (akceptowalne — to dekoracja, nie gameplay).
 *  - Reużywa `PetAnimator` (idle bob + breath + step sway przy velocity)
 *    i `applyVariantFx` (gold/rainbow/huge — pety wystawione mają DOKŁADNIE
 *    ten sam vibe co w inventory).
 *
 * Konstruktor przyjmuje surowe pola z `/stalls/by-instance/:id` — caller
 * (PlazaScene w 5.B.6b) NIE musi wyciągać `PetDef` z configu, wystarczy
 * przekazać `textureKey + variant + paddockBounds`. Power jest opcjonalny —
 * zarezerwowany pod ewentualną etykietę „⚔ X" w przyszłości.
 */
const SPRITE_DISPLAY_SIZE = 40;   // px — trochę mniejsze niż drużyna (48), żeby kilka petów ładnie zmieściło się w paddocku

/** Prędkość spaceru — wolno, gracz patrzy spokojnie. */
const WANDER_SPEED_PX_PER_SEC = 25;

/** Czas pauzy między celami wandera (losowy). */
const WANDER_PAUSE_MIN_MS = 900;
const WANDER_PAUSE_MAX_MS = 2400;

export interface StallPetSpawnOpts {
  /** Tekstura PNG peta (z `PetDef.art.textureKey`). Fallback `placeholder-square`. */
  textureKey: string;
  /** Wariant — sterowanie `applyVariantFx` (normal/gold/rainbow/huge). */
  variant: Variant;
  /**
   * `PetDef.art.scale` — jeśli pet ma manualny override skali w configu
   * (np. większy taun-taun, mniejszy mouse droid), używamy tego mnożnika,
   * żeby paddock pokazywał ten sam vibe co drużyna (5.B.6a Codex LOW 1).
   * Default 1.
   */
  artScale?: number;
  /**
   * Granice paddocku w world coords. Pet wybiera losowe target points
   * wewnątrz tego prostokąta i powoli się do nich przemieszcza.
   */
  bounds: Phaser.Geom.Rectangle;
  /** Logiczne id peta (uid z listingu) — używane do klik-handler w 5.B.6c. */
  petUid: string;
  /** Opcjonalnie — power do przyszłej etykiety. */
  power?: number;
  /**
   * Włącz `setInteractive` (hand cursor + click events). Default `false` —
   * w 5.B.6a sprite jest tylko dekoracją, kursor-bez-akcji to UX trap
   * (5.B.6a Codex LOW 2). 5.B.6c włączył gdy modal kupna jest podpięty.
   */
  interactive?: boolean;
  /**
   * Callback klikania peta (5.B.6c). Wymaga `interactive: true`. Otrzymuje
   * `petUid` — w `PlazaScene` mapuje na (stall_id, listing_pet_uid) i otwiera
   * `BrowseStallModal` z preselekcją tego listingu.
   */
  onClick?: (petUid: string) => void;
  /** Wspólny arbiter hold/tap sceny. Wymagany razem z `onClick`. */
  interactions?: WorldInteractionController;
}

type Mode = 'wandering' | 'paused';

/**
 * Pet w zagrodzie. Sprite + variant FX + idle wander. Wołać `update(dt, now)`
 * w pętli sceny i `destroy()` przy zdejmowaniu (sprzedaż, expire, wyjście
 * z plazy).
 */
export class StallPet {
  readonly sprite: Phaser.GameObjects.Image;
  readonly animator: PetAnimator;
  readonly petUid: string;
  readonly power: number | undefined;

  private readonly bounds: Phaser.Geom.Rectangle;
  /**
   * Efektywny half-size sprite'a po skali (uwzględnia art.scale + huge mult).
   * Używany jako margines przy spawn + target picking — żeby duży HUGE Vader
   * nie wystawał poza paddock (5.B.6a Codex LOW 3). Cache'owane bo nie zmienia
   * się w runtime (variant + artScale = const per spawn).
   */
  private readonly effectiveHalfSize: number;
  private logicalX: number;
  private logicalY: number;
  private prevLogicalX: number;
  private prevLogicalY: number;

  private mode: Mode = 'paused';
  private targetX = 0;
  private targetY = 0;
  /** `now` (ms) kiedy bieżąca pauza powinna się skończyć. */
  private pauseUntil = 0;
  /**
   * Cull state (5.B.6d FIXES Codex MED 1) — gdy paddock off-screen, pet ma
   * `culled=true`: sprite/shadow/aura niewidoczne (Phaser pomija render),
   * tweens gold/rainbow zapauzowane (brak `onUpdate` setTint per klatkę).
   * Bez tego cullowanie tylko `update()` zostawia 192 sprite w batchu
   * renderera + repeat:-1 tweeny na każdym pecie = nie ma realnej oszczędności.
   */
  private culled = false;

  // Variant FX (`VariantFxOwner`) — Pet trzyma mutable rainbowHue + goldShimmerProgress.
  rainbowHue = 0;
  goldShimmerProgress = 0;
  private variantFxRefs: VariantFxRefs | null = null;

  constructor(scene: Phaser.Scene, opts: StallPetSpawnOpts) {
    this.petUid = opts.petUid;
    this.power = opts.power;
    this.bounds = opts.bounds;

    // Effective half-size (5.B.6a Codex LOW 3) — uwzględnia art.scale + huge/
    // titanic mult. Używamy go jako margines przy spawn + target picking, żeby
    // duży pet (np. HUGE Vader z 45% extra / TITANIC z 90%) nie wystawał poza paddock.
    const sizeMult = (opts.artScale ?? 1) * variantScaleMult(opts.variant);
    this.effectiveHalfSize = (SPRITE_DISPLAY_SIZE * sizeMult) / 2;

    // Start w losowym punkcie paddocku Z marginesem — pet od początku
    // mieści się w bounds. Jeśli paddock mniejszy niż pet → spawn w center
    // (clamp fallback).
    const spawnX = this.randomInBoundsX();
    const spawnY = this.randomInBoundsY();
    this.logicalX = spawnX;
    this.logicalY = spawnY;
    this.prevLogicalX = this.logicalX;
    this.prevLogicalY = this.logicalY;

    const tex = scene.textures.exists(opts.textureKey)
      ? opts.textureKey
      : 'placeholder-square';

    this.sprite = scene.add.image(this.logicalX, this.logicalY, tex);
    // Skalowanie — `art.scale` z PetDef + HUGE multiplier. Spójność z `Pet.ts`
    // (drużyna), żeby wystawiony pet wyglądał DOKŁADNIE jak w inventory.
    const baseScale =
      (SPRITE_DISPLAY_SIZE / Math.max(this.sprite.width, this.sprite.height)) * sizeMult;
    this.sprite.setScale(baseScale);
    this.sprite.setDepth(5);
    // `setInteractive` opt-in (5.B.6a Codex LOW 2) — domyślnie OFF, włączane
    // w 5.B.6c gdy klik-handler kupna zostanie podpięty. Hand cursor bez akcji
    // = UX trap.
    if (opts.interactive) {
      this.sprite.setInteractive({ useHandCursor: true });
      // 5.B.6c: hold peta na dotyku / klik myszą → modal kupna.
      if (opts.onClick && opts.interactions) {
        const cb = opts.onClick;
        const uid = this.petUid;
        opts.interactions.bind(this.sprite, () => cb(uid));
      }
    }

    this.animator = new PetAnimator(scene, this.sprite, baseScale, this.logicalX, this.logicalY);

    this.variantFxRefs = applyVariantFx(
      scene,
      this.sprite,
      opts.variant,
      this,
      { x: this.logicalX, y: this.logicalY },
    );

    // Pierwsza pauza startowa — żeby wszystkie spawnowane pety nie ruszyły od razu razem.
    this.pauseUntil = scene.time.now + Phaser.Math.Between(0, WANDER_PAUSE_MAX_MS);
  }

  /**
   * Tick co klatka. `dt` w SEKUNDACH (jak Pet.update). `now` w ms (scene.time.now).
   */
  update(dt: number, now: number): void {
    // 1) Wander tick — wybiera nowy cel albo lerpuje do bieżącego.
    if (this.mode === 'paused') {
      if (now >= this.pauseUntil) {
        this.pickNewTarget();
      }
    } else {
      this.stepTowardTarget(dt, now);
    }

    // 2) Velocity dla animatora.
    const vx = dt > 0 ? (this.logicalX - this.prevLogicalX) / dt : 0;
    const vy = dt > 0 ? (this.logicalY - this.prevLogicalY) / dt : 0;
    this.prevLogicalX = this.logicalX;
    this.prevLogicalY = this.logicalY;

    // 3) Animator offset (bob + step sway).
    const { offsetX, offsetY } = this.animator.update(
      now,
      dt,
      vx,
      vy,
      this.logicalX,
      this.logicalY,
    );

    this.sprite.x = this.logicalX + offsetX;
    this.sprite.y = this.logicalY + offsetY;

    // 4) Aura (jeśli variant FX) śledzi sprite.
    const aura = this.variantFxRefs?.aura;
    if (aura) {
      aura.x = this.sprite.x;
      aura.y = this.sprite.y;
    }
  }

  /** Cleanup — sprite + animator + variant FX. */
  destroy(): void {
    if (this.variantFxRefs) {
      destroyVariantFx(this.variantFxRefs);
      this.variantFxRefs = null;
    }
    this.animator.destroy();
    this.sprite.destroy();
  }

  /**
   * Toggle culling (5.B.6d FIXES Codex MED 1).
   *
   * `culled=true` (paddock poza camera viewport):
   *   - sprite.setVisible(false) → Phaser pomija w `willRender` (skip
   *     submitRender + skip batchowania w WebGL renderer)
   *   - animator.shadow.setVisible(false)
   *   - variantFxRefs.aura.setVisible(false) (jeśli istnieje)
   *   - tweens (gold shimmer / rainbow cycle) pauzowane — repeat:-1 z
   *     onUpdate setTint() per klatkę inaczej leci kosztownie również poza
   *     ekranem
   *
   * `culled=false` — odwrotność (sprite/shadow/aura visible, tweens resume).
   *
   * Idempotentne — wielokrotne wywołanie z tą samą wartością no-op.
   */
  setCulled(culled: boolean): void {
    if (this.culled === culled) return;
    this.culled = culled;
    const visible = !culled;
    this.sprite.setVisible(visible);
    this.animator.shadow.setVisible(visible);
    if (this.variantFxRefs) {
      this.variantFxRefs.aura?.setVisible(visible);
      for (const t of this.variantFxRefs.tweens) {
        if (culled) t.pause();
        else t.resume();
      }
    }
  }

  /** `true` gdy paddock peta jest poza camera viewport (czyt. PlazaScene cull loop). */
  isCulled(): boolean {
    return this.culled;
  }

  /**
   * Aktualizuje granice paddocku w runtime (np. gdy sceny rozszerza paddock).
   * NIE teleportuje peta — jeśli był poza nowymi granicami, najbliższy
   * `pickNewTarget` go „wciągnie" do środka.
   */
  setBounds(bounds: Phaser.Geom.Rectangle): void {
    this.bounds.setTo(bounds.x, bounds.y, bounds.width, bounds.height);
  }

  // ── Wander internals ─────────────────────────────────────────────────────

  /**
   * Losuje X wewnątrz `bounds` z marginesem `effectiveHalfSize` (uwzględnia
   * art.scale + huge mult — 5.B.6a Codex LOW 3). Jeśli paddock węższy niż
   * sprite → fallback do środka (clamp).
   */
  private randomInBoundsX(): number {
    const min = this.bounds.left + this.effectiveHalfSize;
    const max = this.bounds.right - this.effectiveHalfSize;
    if (min >= max) return Math.round((this.bounds.left + this.bounds.right) / 2);
    return Phaser.Math.Between(Math.ceil(min), Math.floor(max));
  }

  /** Analogicznie dla Y. */
  private randomInBoundsY(): number {
    const min = this.bounds.top + this.effectiveHalfSize;
    const max = this.bounds.bottom - this.effectiveHalfSize;
    if (min >= max) return Math.round((this.bounds.top + this.bounds.bottom) / 2);
    return Phaser.Math.Between(Math.ceil(min), Math.floor(max));
  }

  private pickNewTarget(): void {
    this.targetX = this.randomInBoundsX();
    this.targetY = this.randomInBoundsY();
    this.mode = 'wandering';
  }

  private stepTowardTarget(dt: number, now: number): void {
    const dx = this.targetX - this.logicalX;
    const dy = this.targetY - this.logicalY;
    const dist = Math.hypot(dx, dy);
    const step = WANDER_SPEED_PX_PER_SEC * dt;
    if (dist <= step) {
      // Dotarł — pauza, potem nowy cel.
      this.logicalX = this.targetX;
      this.logicalY = this.targetY;
      this.mode = 'paused';
      this.pauseUntil = now + Phaser.Math.Between(WANDER_PAUSE_MIN_MS, WANDER_PAUSE_MAX_MS);
      return;
    }
    this.logicalX += (dx / dist) * step;
    this.logicalY += (dy / dist) * step;
  }
}
